import { Component, ElementRef, Input, OnChanges } from '@angular/core';
import { ColorService } from '@app/shared/services/color.service';
import { HighchartsService } from '@app/shared/services/highcharts.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'dgx-pie-chart',
  templateUrl: './pie-chart.component.html',
})
export class PieChartComponent implements OnChanges {
  @Input() public chartName;
  @Input() public subtitle;
  /*
   * shows an adjustable count at the top that responds to Highcharts legend clicks
   * must pass in an obj with props { i18n: pre-translated variable name, total: number }
   */
  @Input() public count;
  @Input() public trackingName;
  @Input() public series;
  @Input() public location;
  @Input() public isLoading;
  @Input() public chartA11yDescription: string;
  public countStringFormatted: string;
  public legend = [];
  public chartColors = [
    this.colors.getColor('ocean'),
    this.colors.getColor('fuchsia'),
    this.colors.getColor('orange'),
    this.colors.getColor('forest'),
    this.colors.getColor('coral'),
    this.colors.getColor('purple'),
    this.colors.getColor('pink'),
    this.colors.getColor('sky'),
  ];
  public i18n = this.translate.instant(['Core_NoResults']);
  constructor(
    private element: ElementRef,
    private Highcharts: HighchartsService,
    private colors: ColorService,
    private tracker: TrackerService,
    private translate: TranslateService
  ) {}

  public ngOnChanges(): void {
    this.generateChart();
  }

  private updateCount(): void {
    this.countStringFormatted = this.translate.instant(this.count.i18n, {
      count: this.legend.reduce(
        (acc, curr) => acc + (curr.visible ? curr.count : 0),
        0
      ),
      total: this.count.total,
    });
  }

  private trackHover(point) {
    this.tracker.trackEventData({
      action: this.trackingName + ' Pie Chart Hovered',
      properties: {
        Location: this.location,
      },
    });
  }
  private generateChart() {
    if (this.count?.total) {
      this.legend = this.series.map((s) => ({
        name: s[0],
        count: s[1],
        visible: true,
      }));
      this.updateCount();
    } else {
      this.countStringFormatted = null;
    }
    const ctrl = this;
    const container = this.element.nativeElement.querySelector('.highcharts');
    this.Highcharts.createChart(
      container,
      {
        accessibility: {
          enabled: true,
          keyboardNavigation: {
            enabled: true,
          },
          point: {
            descriptionFormatter: (point) => {
              return `${point.name}: ${point.y}`;
            },
          },
        },
        title: {
          text: this.series.length === 0 ? this.i18n.Core_NoResults : null,
          verticalAlign: 'middle',
        },
        chart: {
          style: {
            fontFamily: `'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif`,
          },
        },
        tooltip: {
          formatter() {
            ctrl.trackHover(this);
            return this.key + ' ' + this.y;
          },
        },
        plotOptions: {
          pie: {
            dataLabels: {
              enabled: false,
            },
            innerSize: '60%',
            showInLegend: true,
            point: {
              events: {
                legendItemClick: ($event) => {
                  if (this.count?.total) {
                    const { index } = $event.target;
                    this.legend[index].visible = !this.legend[index].visible;
                    ctrl.updateCount();
                  }
                },
              },
            },
          },
        },
        credits: {
          enabled: false,
        },
        legend: {
          itemStyle: {
            color: 'rgba(15, 31, 44, 0.61)',
            fontWeight: 'Normal',
            textOverflow: null,
          },
          align: 'center',
          verticalAlign: 'bottom',
          navigation: {
            enabled: true,
          },
        },
        series: [
          {
            type: 'pie',
            data: this.series,
            colors: this.chartColors,
          },
        ],
      },
      this.chartName,
      this.chartA11yDescription
    ).subscribe();
  }
}
